/**
 * Persistence for tryout evaluation form templates at
 *   users/{uid}/formTemplates/{tplId}
 *
 * See:
 *   - cheereval/src/pages/TryoutFormCreationNotes.md (Persisting form templates)
 *   - cheereval/agents/admin_user_discussion.md (users/{uid} shape)
 *
 * Document ids use the client-minted `tpl_xxx` from FormEditor so React state
 * ids and Firestore ids stay in sync — writes use setDoc, not addDoc.
 */
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  FirestoreDataConverter,
  getDoc,
  getDocs,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';

import {
  FormSchema,
  FormTemplate,
  normalizeFormSchema,
} from '../types/formSchema';
import { isRecord } from '../utils/isRecord';

const USERS_COLLECTION = 'users';
const FORM_TEMPLATES_SUBCOLLECTION = 'formTemplates';

function templatesCollectionRef(db: Firestore, uid: string) {
  return collection(db, USERS_COLLECTION, uid, FORM_TEMPLATES_SUBCOLLECTION);
}

function templateDocRef(db: Firestore, uid: string, tplId: string) {
  return doc(db, USERS_COLLECTION, uid, FORM_TEMPLATES_SUBCOLLECTION, tplId);
}

function readTimestamp(value: unknown): Timestamp | null {
  return value instanceof Timestamp ? value : null;
}

export function parseTemplateDoc(id: string, data: unknown): FormTemplate {
  const rec = isRecord(data) ? data : {};
  const schemaGroups = isRecord(rec.schema) && Array.isArray(rec.schema.groups)
    ? rec.schema.groups as FormSchema['groups']
    : [];
  return {
    id,
    name: typeof rec.name === 'string' ? rec.name : '',
    schema: normalizeFormSchema({ groups: schemaGroups }),
    createdAt: readTimestamp(rec.createdAt),
    lastUpdated: readTimestamp(rec.lastUpdated),
  };
}

export const templateConverter: FirestoreDataConverter<FormTemplate> = {
  toFirestore: ({ id: _id, ...data }) => data,
  fromFirestore: (snap: QueryDocumentSnapshot) => parseTemplateDoc(snap.id, snap.data()),
};

/** Load all templates owned by this user. One-shot (no onSnapshot). */
export async function fetchFormTemplates(
  db: Firestore,
  uid: string,
): Promise<FormTemplate[]> {
  const snapshot = await getDocs(templatesCollectionRef(db, uid).withConverter(templateConverter));
  const items = snapshot.docs.map((d) => d.data());
  items.sort((a, b) => {
    const aTime = a.lastUpdated?.toMillis() ?? 0;
    const bTime = b.lastUpdated?.toMillis() ?? 0;
    return bTime - aTime;
  });
  return items;
}

/**
 * Create or update a template at users/{uid}/formTemplates/{template.id}.
 *
 * - On first write: sets `createdAt` and `lastUpdated` to server time.
 * - On subsequent writes: refreshes `lastUpdated` only; `createdAt` is left
 *   untouched so it stays the original creation time.
 */
export async function saveFormTemplate(
  db: Firestore,
  uid: string,
  template: FormTemplate,
): Promise<void> {
  const ref = templateDocRef(db, uid, template.id);
  const existing = await getDoc(ref);

  const base = {
    name: template.name,
    schema: template.schema,
    lastUpdated: serverTimestamp(),
  };

  if (existing.exists()) {
    await updateDoc(ref, base);
    return;
  }

  await setDoc(ref, {
    ...base,
    createdAt: serverTimestamp(),
  });
}

export async function deleteFormTemplate(
  db: Firestore,
  uid: string,
  tplId: string,
): Promise<void> {
  await deleteDoc(templateDocRef(db, uid, tplId));
}
